import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { restaurantInfo } from '../data/mock';
import { track } from '../lib/track';

const STORAGE_KEY = 'familys_app_popup_last';
const COOLDOWN_MS = 3 * 24 * 3600 * 1000; // retente au bout de 3 jours (décision Faiçal 2026-08-20)
const SHOW_DELAY_MS = 2800;

/**
 * Popup de promotion de l'appli mobile.
 *
 * Règles d'affichage (pour ne pas harceler le visiteur) :
 *  - au plus 1 fois tous les 3 jours (localStorage) ;
 *  - jamais sur /application (il y est déjà) ni sur les pages légales ;
 *  - jamais si une annonce (annonce.json active) est déjà affichée ;
 *  - apparaît ~3 s après l'arrivée, le temps de voir le site.
 * Détecte iPhone/Android pour mettre le bon store en avant.
 */
const AppDownloadPopup = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  const platform = (() => {
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    return 'other';
  })();

  useEffect(() => {
    const excluded = ['/application', '/mentions-legales', '/cgv'];
    if (excluded.includes(location.pathname)) return;

    // Mode aperçu : ?popup dans l'adresse force l'affichage (sans compter
    // dans les stats ni toucher au cooldown) — pratique pour vérifier/montrer.
    const force = new URLSearchParams(window.location.search).has('popup');

    let last = 0;
    try { last = Number(localStorage.getItem(STORAGE_KEY)) || 0; } catch (e) { /* navigation privée */ }
    if (!force && Date.now() - last < COOLDOWN_MS) return;

    let cancelled = false;
    const timer = setTimeout(() => {
      // L'annonce (congés…) est prioritaire : si elle est active, on s'efface.
      fetch(`${process.env.PUBLIC_URL}/annonce.json?ts=${Date.now()}`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
        .then((data) => {
          if (!cancelled && (force || !(data && data.active))) {
            setVisible(true);
            if (!force) track('popup_shown');
          }
        });
    }, force ? 400 : SHOW_DELAY_MS);

    return () => { cancelled = true; clearTimeout(timer); };
    // volontairement une seule fois par chargement, pas à chaque navigation
  }, []);

  const dismiss = (event) => {
    if (typeof event === 'string') track(event);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (e) { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  const showIos = platform !== 'android';
  const showAndroid = platform !== 'ios';

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-end sm:items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={() => dismiss('click_close')}
    >
      <div
        className="app-popup-card relative w-full max-w-sm overflow-hidden rounded-3xl bg-gradient-to-b from-red-950 via-orange-950 to-amber-950 text-center shadow-2xl border border-orange-400/30"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute right-3 top-2 z-10 text-3xl leading-none text-white/60 transition-colors hover:text-white"
          onClick={() => dismiss('click_close')}
          aria-label="Fermer"
        >
          &times;
        </button>

        {/* Visuel de l'app + badge fidélité */}
        <div className="relative flex justify-center pt-6">
          <img
            src={`${process.env.PUBLIC_URL}/images/app-1.webp`}
            alt="Application Family's"
            className="h-44 w-auto rounded-2xl shadow-2xl"
            loading="lazy"
          />
          <div className="absolute right-6 top-4 rotate-12 rounded-full bg-yellow-400 px-3 py-1.5 text-sm font-bold text-gray-900 shadow-lg">
            5 % sur ta carte
          </div>
        </div>

        <div className="px-6 pb-6 pt-4">
          <h3 className="mb-1 text-2xl font-extrabold text-white">
            Commande, joue <span className="text-orange-400">&amp; gagne !</span>
          </h3>
          <p className="mb-4 text-sm leading-relaxed text-gray-300">
            Commande en 30 secondes sur l'appli, cumule <strong className="text-yellow-300">5 % crédités sur ta carte de fidélité</strong> à
            chaque commande et tente ta chance à la roue de la fortune 🎡
          </p>

          {/* Badges stores — le bon store en premier selon le téléphone */}
          <div className="flex flex-col items-center gap-3">
            {showIos && (
              <a href={restaurantInfo.appLinks.ios} target="_blank" rel="noopener noreferrer" onClick={() => dismiss('click_ios')}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/appstore-badge.svg`}
                  alt="Télécharger sur l'App Store"
                  className="h-12 w-auto transition-transform hover:scale-105"
                />
              </a>
            )}
            {showAndroid && (
              <a href={restaurantInfo.appLinks.android} target="_blank" rel="noopener noreferrer" onClick={() => dismiss('click_android')}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/googleplay-badge.png`}
                  alt="Disponible sur Google Play"
                  className="h-12 w-auto transition-transform hover:scale-105"
                />
              </a>
            )}
          </div>

          <div className="mt-4 flex items-center justify-center gap-4 text-sm">
            <Link to="/application" onClick={() => dismiss('click_more')} className="text-orange-300 underline-offset-2 hover:underline">
              En savoir plus
            </Link>
            <button onClick={() => dismiss('click_later')} className="text-white/50 transition-colors hover:text-white">
              Plus tard
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .app-popup-card { animation: appPopupUp .45s cubic-bezier(.16,1,.3,1) both; }
        @keyframes appPopupUp {
          from { opacity: 0; transform: translateY(48px) scale(.96); }
          to   { opacity: 1; transform: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .app-popup-card { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default AppDownloadPopup;
